import { inject, Injectable, NgZone } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IdDescriptionItem, ISourceItemDataExtended } from '@se/models';
import { Guid, Logger } from '@se/se.platform.base';
import EstensaModel from 'estensa-module';
import { find } from 'lodash-es';
import moment from 'moment';
import { combineLatest, defer, EMPTY, firstValueFrom, fromEventPattern, Observable, Subject } from 'rxjs';
import {
    bufferTime,
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    share,
    shareReplay,
    skip,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { selectSignalrConnected } from '../connectivity/connectivity.selectors';
import {
    AlarmSeverity,
    AssociationNotification,
    EcgNotification,
    isAssociationNotification,
    isEcgNotification,
    isMpsNotification,
    isSISS3Notification,
    isStoreNotification,
    isUS2AINotification,
    isWorklistEcgNotification,
    isWorklistExecutionNotification,
    isWorklistReportingNotification,
    MpsNotification,
    Notification,
    SignalRNotificationType,
    SISS3Notification,
    StoreNotification,
    US2AINotification,
    WorklistEcgNotification,
    WorklistExecutionNotification,
    WorklistNotification,
    WorklistReportingNotification,
} from '../models/signalr-notifications.models';
import {
    seEcgAlarm,
    seEcgAlarmWarning,
    seExaminationAssociation,
    seExaminationStore,
    seUS2AIAlert,
} from '../root-store/root.actions';
import * as fromRoot from '../root-store/root.state';
import {
    seSearch,
    seSearchIfFiltersEq,
    seSearchRemoveItems,
    seSearchUpdateItems,
} from '../search-page/redux/search-page.actions';
import { SearchFormType } from '../search-page/search-page-type.enum';
import { convertPropertiesToCamelCase, fromServerIsoString } from '../utils/interop';
import { RouterStackService } from '../utils/routing/router-stack.service';
import { EstensaBusWrapperService } from './angularjs/estensa-bus-wrapper.service';
import { EstensaCollectionService } from './angularjs/estensa-collection.service';
import { EstensaNotificationAuthenticatedHubService } from './angularjs/estensa-notification-authenticated-hub.service';
import { EstensaNotificationService } from './angularjs/estensa-notification.service';
import { AuditService, AuditType } from './audit.service';
import { AuthenticationService } from './authentication/authentication.service';
import { EcgService } from './rest/ecg.service';
import { RadioSettingsService } from './settings/radio-settings.service';

interface EstensaWebNotification {
    correlationId: string;
    date: string;
    notification: Notification;
}

const getIDateRangeFromWorklistNotifications = (
    notifications: WorklistNotification[],
): { from: string; to: string } => {
    const eventDates = notifications.map((n) => n.eventDate);
    const minDateString = eventDates.reduce((min, current) => (moment(current).isBefore(moment(min)) ? current : min));
    const maxDateString = eventDates.reduce((max, current) => (moment(current).isAfter(moment(max)) ? current : max));
    return {
        from: minDateString,
        to: maxDateString,
    };
};

@Injectable({
    providedIn: 'root',
})
export class SignalrNotificationsService {
    private readonly auditService = inject(AuditService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly collectionService = inject(EstensaCollectionService);
    private readonly estensaBus = inject(EstensaBusWrapperService);
    private readonly estensaNotification = inject(EstensaNotificationService);
    private readonly logger = inject(Logger);
    private readonly notificationHub = inject(EstensaNotificationAuthenticatedHubService);
    private readonly store = inject<Store<fromRoot.RootState>>(Store);
    private readonly radioSettingsService = inject(RadioSettingsService);
    private readonly routerStack = inject(RouterStackService);
    private readonly ecgRestService = inject(EcgService);
    private readonly translate = inject(TranslateService);
    private readonly zone = inject(NgZone);

    private readonly notification$ = new Subject<EstensaWebNotification>();

    constructor() {
        this.notificationHub.on('notifyAll', (evt) => {
            this.zone.run(() => {
                this.notification$.next(convertPropertiesToCamelCase<EstensaWebNotification>(evt));
            });
        });
    }

    private readonly areAlarmsEnabled$ = this.radioSettingsService.radioSettingsAsync.pipe(
        map((settings) => settings.ecgSettings.enableAlerts),
    );

    private readonly isSignalrConnected$ = this.store.pipe(
        select(selectSignalrConnected),
        skip(1),
        debounceTime(15000),
        distinctUntilChanged(),
    );

    private readonly ecgBusinessUnits$ = defer(() =>
        this.collectionService.get('ListEcg', 'GetListItems?lsName=lsBusinessUnit' as 'GetListItems'),
    ).pipe(
        // garantisci che i subscriber futuri ricevano l’ultimo valore
        shareReplay(1),
    );

    private readonly executionBusinessUnits$ = defer(() =>
        this.collectionService.get('ListWorkFlow', 'GetListItems?lsName=lsBusinessUnit' as 'GetListItems'),
    ).pipe(
        // garantisci che i subscriber futuri ricevano l’ultimo valore
        shareReplay(1),
    );

    private readonly specialtyBusinessUnits$ = defer(() =>
        this.collectionService.get('ListCpReporting', 'GetListItems?lsName=lsBusinessUnit' as 'GetListItems'),
    ).pipe(
        // garantisci che i subscriber futuri ricevano l’ultimo valore
        shareReplay(1),
    );

    private readonly cardioBusinessUnits$ = defer(() => this.collectionService.get('List', 'GetBusinessUnits')).pipe(
        // garantisci che i subscriber futuri ricevano l’ultimo valore
        shareReplay(1),
    );

    /** le notifiche generiche vanno gestite se il campo idUsers è null o un array vuoto o se l'id dell'utente loggato è nell'array idUsers */
    private shouldProcessNotification = ({ notification, correlationId }: EstensaWebNotification): boolean => {
        if (!notification.idUsers || notification.idUsers.length === 0) return true;
        const idUser = this.authenticationService.getUserId();
        if (notification.idUsers.includes(idUser)) return true;
        this.logger.debug(`Notification discarded for current user (user id: ${idUser})`, correlationId);
        return false;
    };

    /** le notifiche di worklist vanno gestite solo se id dell'utente loggato è differente da quello di idUser (utente che ha lanciato l'evento da notificare) */
    private shouldProcessWorklistNotification = ({
        notification,
        correlationId,
    }: Omit<EstensaWebNotification, 'notification'> & { notification: WorklistNotification }): boolean => {
        const idUser = this.authenticationService.getUserId();
        if (notification.idUser === idUser) {
            this.logger.debug(`Notification discarded (user has started the notification)`, correlationId);
            return false;
        }
        return true;
    };

    /** le notifiche di worklist vanno gestite confrontando i centri di costo (businessUnit) per il quale l'utente e la worklist è abilitata e la business unit che ha lanciato l'evento */
    private shouldProcessWorklistNotificationByBusinessUnits = ([{ notification, correlationId }, businessUnits]: [
        Omit<EstensaWebNotification, 'notification'> & { notification: WorklistNotification },
        EstensaModel.BusinessUnit[] | ISourceItemDataExtended[] | IdDescriptionItem[],
    ]): boolean => {
        const businessUnitsIds = businessUnits.map((item) => {
            if ('id' in item) {
                return item.id;
            } else {
                return item.guid;
            }
        });
        const isUserInBusinessUnit = businessUnitsIds.some((id) => id === notification.idBusinessUnit);
        if (!isUserInBusinessUnit)
            this.logger.debug(
                `Notification discarded (user does not belong to business unit "${notification.idBusinessUnit}")`,
                correlationId,
            );
        return isUserInBusinessUnit;
    };

    /** trasformo le notifiche signalR in uno stream rxjs */
    private readonly signalREvent$ = this.notification$.pipe(
        map((event) => {
            /** è necessario convertire il case perché SignalR non utilizza il JsonFormatter di Estensa.Web.Rest */
            return convertPropertiesToCamelCase<EstensaWebNotification>(event);
        }),
        tap(({ notification, correlationId }) => {
            this.logger.info(
                `Received notification on client.\n\ttype: ${notification.notificationType}`,
                correlationId,
            );
            if (
                !Object.values(SignalRNotificationType)
                    .filter((value) => typeof value === 'number')
                    .includes(notification.notificationType)
            ) {
                this.logger.error(`Unknown notification type: ${notification.notificationType}`, correlationId);
            }
        }),
        /** filtro le notifiche globali */
        filter(this.shouldProcessNotification),
        catchError((error) => {
            this.logger.error(`Error while processing notification: ${error.message}`, error?.CorrelationId);
            return EMPTY;
        }),
        /** 👇 rende la sorgente condivisa (un solo handler al hub) */
        share(),
    );

    private readonly createNotificationStream = <TNotification extends Notification>(
        predicate: (notification: Notification) => notification is TNotification,
    ): Observable<Omit<EstensaWebNotification, 'notification'> & { notification: TNotification }> =>
        this.signalREvent$.pipe(
            filter((event): event is Omit<EstensaWebNotification, 'notification'> & { notification: TNotification } =>
                predicate(event.notification),
            ),
        );

    /** stream rxjs per notifiche ecg */
    private readonly ecgNotification$ = this.createNotificationStream(isEcgNotification).pipe(
        tap(({ notification, correlationId }) =>
            this.logger.debug(`Received ecg notification: ${notification.idExamination}`, correlationId),
        ),
    );

    /** stream rxjs per notifiche mps */
    private readonly mpsNotification$ = this.createNotificationStream(isMpsNotification).pipe(
        tap(({ correlationId }) => this.logger.debug(`Received mps notification`, correlationId)),
    );

    /** stream rxjs per notifiche association */
    private readonly associationNotification$ = this.createNotificationStream(isAssociationNotification).pipe(
        tap(({ notification, correlationId }) =>
            this.logger.debug(
                `Received association notification for examination ${notification.idExamination}`,
                correlationId,
            ),
        ),
    );

    /** stream rxjs per notifiche worklist ecg */
    private readonly worklistEcgNotification$ = this.createNotificationStream(isWorklistEcgNotification).pipe(
        filter((data) => this.shouldProcessWorklistNotification(data)),
        withLatestFrom(this.ecgBusinessUnits$),
        filter((data) => this.shouldProcessWorklistNotificationByBusinessUnits(data)),
        map(([notification]) => notification),
        tap(({ correlationId }) => this.logger.debug('Received worklist ecg refresh notification', correlationId)),
    );

    /** stream rxjs per notifiche worklist execution */
    private readonly worklistExecutionNotification$ = this.createNotificationStream(
        isWorklistExecutionNotification,
    ).pipe(
        filter((data) => this.shouldProcessWorklistNotification(data)),
        withLatestFrom(this.executionBusinessUnits$),
        filter((data) => this.shouldProcessWorklistNotificationByBusinessUnits(data)),
        map(([notification]) => notification),
        tap(({ correlationId }) =>
            this.logger.debug('Received worklist execution refresh notification', correlationId),
        ),
        bufferTime(10000),
        filter((batch) => batch.length > 0),
    );

    /** stream rxjs per notifiche worklist specialty */
    private readonly worklistSpecialtyNotification$ = this.createNotificationStream(
        isWorklistReportingNotification,
    ).pipe(
        filter((data) => this.shouldProcessWorklistNotification(data)),
        withLatestFrom(this.specialtyBusinessUnits$),
        filter((data) => this.shouldProcessWorklistNotificationByBusinessUnits(data)),
        map(([notification]) => notification),
        tap(({ correlationId }) => {
            this.logger.debug('Received worklist specialty refresh notification', correlationId);
        }),
        bufferTime(10000),
        filter((batch) => batch.length > 0),
    );

    /** stream rxjs per notifiche worklist cardio */
    private readonly worklistCardionNotification$ = this.createNotificationStream(isWorklistReportingNotification).pipe(
        filter((data) => this.shouldProcessWorklistNotification(data)),
        withLatestFrom(this.cardioBusinessUnits$),
        filter((data) => this.shouldProcessWorklistNotificationByBusinessUnits(data)),
        map(([notification]) => notification),
        tap(({ correlationId }) => {
            this.logger.debug('Received worklist specialty refresh notification', correlationId);
        }),
        bufferTime(10000),
        filter((batch) => batch.length > 0),
    );

    /** stream rxjs per notifiche siss3  */
    private readonly SISS3Notification$ = this.createNotificationStream(isSISS3Notification).pipe(
        tap(({ correlationId }) => this.logger.debug(`Received SISS3 notification`, correlationId)),
    );

    /** stream rxjs per notifiche us2ai */
    private readonly US2AINotification$ = this.createNotificationStream(isUS2AINotification).pipe(
        tap(({ correlationId }) => this.logger.debug(`Received US2AI notification`, correlationId)),
    );

    /** stream rxjs per notifiche store */
    private readonly storeNotification$ = this.createNotificationStream(isStoreNotification).pipe(
        tap(({ notification, correlationId }) =>
            this.logger.debug(
                `Received Store notification for examination ${notification.idExamination}`,
                correlationId,
            ),
        ),
    );

    private pendingEcgAlarms: { notificationId: number; alarms: EcgNotification[] } | null = null;
    private approvedEcgAlarm: number | null = null;
    private disconnectionAlarm: number | null = null;

    init() {
        this.ecgNotification$.subscribe(({ notification, correlationId }) =>
            this.onEcgNotification(notification, correlationId),
        );

        this.mpsNotification$.subscribe(({ notification }) => this.onMpsQueueNotification(notification));

        this.associationNotification$.subscribe(({ notification }) => this.onAssociation(notification));

        this.worklistEcgNotification$.subscribe(({ notification }) => this.onWorklistEcgNotification(notification));

        this.worklistExecutionNotification$.subscribe((events) => {
            const notifications = events.map((e) => e.notification);
            this.onWorklistExecutionNotifications(notifications);
        });

        this.worklistSpecialtyNotification$.subscribe((events) => {
            const notifications = events.map((e) => e.notification);
            this.onWorklistSpecialtyNotifications(notifications);
        });

        this.worklistCardionNotification$.subscribe((events) => {
            const notifications = events.map((e) => e.notification);
            this.onWorklistCardioNotifications(notifications);
        });

        this.SISS3Notification$.subscribe(({ notification }) => this.onSISS3Notification(notification));

        this.US2AINotification$.subscribe(({ notification }) => this.onUS2AINotification(notification));

        this.storeNotification$.subscribe(({ notification }) => this.onStoreNotification(notification));

        combineLatest([this.isSignalrConnected$, this.areAlarmsEnabled$]).subscribe(
            ([isSignalrConnected, enableAlerts]) => this.onSignalrConnectionChanged(isSignalrConnected, enableAlerts),
        );
    }

    /** ecg gestione notifiche non gestite in avvio */
    async getEcgActiveAlarms(ignoreTokenErrors: boolean = false) {
        const pendingEcgNotifications = await firstValueFrom(
            this.ecgRestService.getEcgNotifications(ignoreTokenErrors),
        );

        return Promise.all(
            pendingEcgNotifications.map((notification) => this.onEcgNotification(notification, new Guid().toString())),
        );
    }

    /** ==================== INIZIO: gestione notifiche ecg ==================== */
    private onEcgNotification(notification: EcgNotification, correlationId: string) {
        switch (notification.severityType) {
            case AlarmSeverity.Alert:
                return this.startEcgAlarm(notification, correlationId);
            case AlarmSeverity.StopAlert:
                return this.stopEcgAlarm(notification, correlationId);
            case AlarmSeverity.TemporizedWarning:
                return this.showWarning(notification, correlationId);
            case AlarmSeverity.ExamApproved:
                return this.showApprovedNotification(notification, correlationId);
            default:
                this.logger.error(
                    `Notification discarded (unknown severity type ${notification.severityType})`,
                    correlationId,
                );
        }
    }

    private startEcgAlarm(alarm: EcgNotification, correlationId: string) {
        this.auditService
            .storeUserAudit(AuditType.AlertReceived, `Ecg vNext Alert Received (IdExam: ${alarm.idExamination})`)
            .subscribe();

        if (find(this.pendingEcgAlarms?.alarms, (a) => a.idExamination == alarm.idExamination)) {
            this.logger.warn(
                `Notification discarded (alarm already active for examination ${alarm.idExamination})`,
                correlationId,
            );
            return;
        }

        this.logger.info('Starting alarm on client', correlationId);

        if (this.pendingEcgAlarms) {
            this.pendingEcgAlarms.alarms.push(alarm);
            this.estensaNotification.editPersistentnotification(this.pendingEcgAlarms.notificationId, {
                description: this.getAlarmNotificationDescription(this.pendingEcgAlarms.alarms.length),
            });
        } else {
            const notificationId = this.estensaNotification.insertPersistentNotification({
                description: this.getAlarmNotificationDescription(1),
                onClick: () => this.goToPendingEcgAlarms(correlationId),
            });
            this.pendingEcgAlarms = {
                notificationId,
                alarms: [alarm],
            };
        }
    }

    private stopEcgAlarm(alarm: EcgNotification, correlationId: string) {
        if (!this.pendingEcgAlarms) {
            return;
        }
        this.logger.info('Stopping alarm on client', correlationId);

        const activeAlarms = this.pendingEcgAlarms.alarms.filter((a) => a.idExamination !== alarm.idExamination);
        if (activeAlarms.length === 0) {
            this.estensaNotification.clearPersistentNotification(this.pendingEcgAlarms.notificationId);
            this.pendingEcgAlarms = null;
        } else {
            this.pendingEcgAlarms.alarms = activeAlarms;
            this.estensaNotification.editPersistentnotification(this.pendingEcgAlarms.notificationId, {
                description: this.getAlarmNotificationDescription(this.pendingEcgAlarms.alarms.length),
            });
        }
    }

    private showWarning(alarm: EcgNotification, correlationId: string) {
        this.logger.info('Showing temporized warning on client...', correlationId);
        this.auditService
            .storeUserAudit(AuditType.WarningReceived, `Ecg vNext Warning Received (IdExam: ${alarm.idExamination})`)
            .subscribe();

        this.store.dispatch(seEcgAlarmWarning({ alarm, correlationId: correlationId }));
    }

    private showApprovedNotification(alarm: EcgNotification, correlationId: string) {
        if (this.approvedEcgAlarm !== null) {
            this.logger.info('Exam approved notification is already active', correlationId);
            return;
        }

        this.logger.info('Showing exam approved notification', correlationId);

        this.approvedEcgAlarm = this.estensaNotification.insertPersistentNotification({
            description: this.translate.instant('EcgAlarmNewApprovedReport'),
            onClick: () => {
                this.approvedEcgAlarm = null;
                this.goToPatientFolder(alarm);
            },
            onDismiss: () => {
                this.approvedEcgAlarm = null;
            },
        });
    }

    private goToPatientFolder(_: EcgNotification) {
        const lastRouterStackState = this.routerStack.lastState;
        const currentStateData = lastRouterStackState
            ? {
                  url: lastRouterStackState.to,
                  params: lastRouterStackState.params,
              }
            : null;

        this.estensaBus.notify({
            messageType: 'NG2_ROUTER_NAVIGATION_REQUEST',
            data: {
                state: 'cardiology.search',
                currentState: currentStateData,
                params: {
                    clean: true,
                    search: true,
                    patient: null,
                    cleanComeBackStateHistory: null,
                    ignoreRouteChangeEvent: true,
                },
            },
        });
    }

    private goToPendingEcgAlarms(correlationId: string) {
        if (!this.pendingEcgAlarms) {
            // Potrebbe essere per es. un doppio click.
            return;
        }
        const pending = this.pendingEcgAlarms;
        this.pendingEcgAlarms = null;
        this.logger.info('Alarm accepted', correlationId);

        // TODO: Usare un meccanismo più resiliente.
        // Aggiungi gli ecg alla pagina se si sta già refertando.
        if (this.routerStack.url === '/new/vnext/ecg/reporting-details') {
            for (const alarm of pending.alarms) {
                this.store.dispatch(seEcgAlarm({ alarm, correlationId }));
            }
            this.estensaNotification.clearPersistentNotification(pending.notificationId);
            return;
        }

        this.zone.run(() => {
            this.routerStack
                .go('/new/vnext/ecg/reporting-details', {
                    activationAlarms: pending.alarms,
                    exams: pending.alarms.map((alarm) => ({
                        examinationId: alarm.idExamination,
                        patient: alarm.patient,
                        examinationDate: fromServerIsoString(alarm.examinationDate).toDate(),
                        withEcg: true,
                    })),
                })
                .then((hasNavigated) => {
                    if (hasNavigated) {
                        this.pendingEcgAlarms = null;
                        this.estensaNotification.clearPersistentNotification(pending.notificationId);
                    } else {
                        this.pendingEcgAlarms = pending;
                    }
                });
        });
    }

    private getAlarmNotificationDescription(numAlarms: number) {
        if (numAlarms > 1) {
            return `${this.translate.instant('EcgAlarmStart')} (${numAlarms})`;
        }

        return this.translate.instant('EcgAlarmStart');
    }

    /** ==================== FINE: gestione notifiche ecg ==================== */

    /** ==================== gestione notifiche MPS ==================== */
    private onMpsQueueNotification(notification: MpsNotification) {
        let update: boolean = !notification.delete && !notification.add;
        if (update) {
            this.store.dispatch(
                seSearchUpdateItems({
                    formType: SearchFormType.ON_PRINTING_CD,
                    items: [notification.queue],
                    idPropertyName: 'jobID',
                    highlightRow: true,
                }),
            );
        } else if (notification.delete && !notification.add) {
            this.store.dispatch(
                seSearchRemoveItems({
                    formType: SearchFormType.ON_PRINTING_CD,
                    idList: [notification.id],
                    idPropertyName: 'jobID',
                }),
            );
        } else if (notification.add && !notification.delete) {
            this.store.dispatch(
                seSearch({
                    formType: SearchFormType.ON_PRINTING_CD,
                    highlightRow: true,
                }),
            );
        } else {
            // Notifica stato di errore
        }
    }

    /** ==================== gestione notifiche Association ==================== */
    private onAssociation(notification: AssociationNotification) {
        this.store.dispatch(
            seExaminationAssociation({
                idExamination: notification.idExamination,
                associationType: notification.associationType,
            }),
        );
    }

    /** ==================== gestione notifiche Worklist ECG ==================== */
    private onWorklistEcgNotification(notification: WorklistEcgNotification) {
        this.store.dispatch(
            seSearchIfFiltersEq({
                formType: SearchFormType.ECG,
                highlightRow: true,
                showDiff: true,
                filters: {
                    date: notification.eventDate,
                },
            }),
        );
    }

    /** ==================== gestione notifiche Worklist Execution  gestite bufferizzate ==================== */
    private onWorklistExecutionNotifications(notifications: WorklistExecutionNotification[]) {
        this.store.dispatch(
            seSearchIfFiltersEq({
                formType: SearchFormType.EXECUTION,
                highlightRow: true,
                showDiff: true,
                filters: {
                    fromTo: getIDateRangeFromWorklistNotifications(notifications),
                },
            }),
        );
    }

    /** ==================== gestione notifiche Worklist Specialty gestite bufferizzate ==================== */
    private onWorklistSpecialtyNotifications(notifications: WorklistReportingNotification[]) {
        this.store.dispatch(
            seSearchIfFiltersEq({
                formType: SearchFormType.SPECIALTY,
                highlightRow: true,
                showDiff: true,
                filters: {
                    fromTo: getIDateRangeFromWorklistNotifications(notifications),
                },
            }),
        );
    }

    /** ==================== gestione notifiche Worklist Cardio gestite bufferizzate ==================== */
    private onWorklistCardioNotifications(notifications: WorklistReportingNotification[]) {
        this.store.dispatch(
            seSearchIfFiltersEq({
                formType: SearchFormType.VISIT,
                highlightRow: true,
                showDiff: true,
                filters: {
                    fromTo: getIDateRangeFromWorklistNotifications(notifications),
                },
            }),
        );
    }

    /** ==================== gestione notifiche SISS3 ==================== */
    private onSISS3Notification(notification: SISS3Notification) {
        this.logger.warn(`Notification not yet handled.\n\ttype: ${notification.notificationType}`);
    }

    /** ==================== gestione notifiche US2AI ==================== */
    private onUS2AINotification(notification: US2AINotification) {
        const { idRepository, studyInstanceUid } = notification;
        this.store.dispatch(seUS2AIAlert({ idRepository, studyInstanceUid }));
    }

    /** ==================== gestione notifiche Store ==================== */
    private onStoreNotification(notification: StoreNotification) {
        this.store.dispatch(
            seExaminationStore({
                idExamination: notification.idExamination,
            }),
        );
    }

    /** ==================== gestione signalR connection changed ==================== */
    private onSignalrConnectionChanged(isConnected: boolean, enableAlerts: boolean) {
        this.logger.info(
            `SignalrNotificationsService: SignalR: ${isConnected ? 'connected' : 'disconnected'}; ECG alerts: ${
                enableAlerts ? 'enabled' : 'disabled'
            }`,
        );
        if (!enableAlerts) {
            return;
        }

        if (!isConnected && this.disconnectionAlarm === null) {
            this.disconnectionAlarm = this.estensaNotification.insertPersistentNotification({
                description: this.translate.instant('Disconnected'),
                onClick: () => (this.disconnectionAlarm = null),
            });
        } else if (isConnected && this.disconnectionAlarm !== null) {
            this.estensaNotification.clearPersistentNotification(this.disconnectionAlarm);
            this.disconnectionAlarm = null;
        }
    }
}
